import { DoctorRow, DxClaim } from './types';
import {
  compareVisitDatesDesc,
  doctorDedupKey,
  pickMostRecentVisitDate,
} from './visitDateUtils';

const mergeDxClaim = (existing: DxClaim, incoming: DxClaim): DxClaim => {
  const visitDate = pickMostRecentVisitDate(existing.visitDate, incoming.visitDate);
  const preferIncoming =
    compareVisitDatesDesc(incoming.visitDate, existing.visitDate) < 0;
  return { ...(preferIncoming ? incoming : existing), visitDate };
};

const mergeRxRow = (existing: DoctorRow, incoming: DoctorRow): DoctorRow => {
  const visitDate = pickMostRecentVisitDate(existing.visitDate, incoming.visitDate);
  const preferIncoming =
    compareVisitDatesDesc(incoming.visitDate, existing.visitDate) < 0;
  return { ...(preferIncoming ? incoming : existing), visitDate };
};

/**
 * Collapses multiple DX claim rows for the same physician into one row per
 * unique doctor (NPI primary, name+address fallback), keeping fields from the
 * most recent visit.
 */
export const deduplicateDxClaims = (claims: DxClaim[]): DxClaim[] => {
  const map = new Map<string, DxClaim>();
  const order: string[] = [];

  for (const claim of claims) {
    const key = doctorDedupKey(claim.npi, claim.name, claim.address);
    const existing = map.get(key);

    if (existing) {
      map.set(key, mergeDxClaim(existing, claim));
    } else {
      map.set(key, { ...claim });
      order.push(key);
    }
  }

  return order
    .map(k => map.get(k)!)
    .sort((a, b) => compareVisitDatesDesc(a.visitDate, b.visitDate));
};

export const sortRxRowsByVisitDate = (rows: DoctorRow[]): DoctorRow[] =>
  [...rows].sort((a, b) => compareVisitDatesDesc(a.visitDate, b.visitDate));

/**
 * Collapses multiple RX rows for the same physician into one row per unique
 * doctor (NPI primary, name+address fallback), keeping fields from the most
 * recent fill date.
 */
export const deduplicateRxDoctors = (rows: DoctorRow[]): DoctorRow[] => {
  const map = new Map<string, DoctorRow>();
  const order: string[] = [];

  for (const row of rows) {
    const key = doctorDedupKey(row.npi, row.name, row.address);
    const existing = map.get(key);

    if (existing) {
      map.set(key, mergeRxRow(existing, row));
    } else {
      map.set(key, { ...row });
      order.push(key);
    }
  }

  return order
    .map(k => map.get(k)!)
    .sort((a, b) => compareVisitDatesDesc(a.visitDate, b.visitDate));
};

/** Prepare All Doctors tab rows: deduped DX + deduped RX. */
export const prepareAllDoctorsRows = (
  dxClaims: DxClaim[],
  rxRows: DoctorRow[],
) => ({
  dxRows: deduplicateDxClaims(dxClaims),
  rxRows: deduplicateRxDoctors(rxRows),
});
